import {
  Datagrid,
  ExportButton,
  FunctionField,
  List,
  Pagination,
  ReferenceField,
  TextField,
  TextInput,
  TopToolbar,
} from "react-admin";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { PageTopBreadcrumb } from "../../components/PageTopBreadcrumb";
import { RaRowActions } from "../../components/RaRowActions";
import { AppBulkDeleteButton } from "../../components/AppBulkDeleteButton";
import { ListPageIndicator } from "../../components/TablePageIndicator";
import { RoleStatusChip } from "../../components/RoleStatusChip";
import { downloadCsvRows, toCsvRows } from "../../components/csvExporter";
import {
  datagridSx,
  listCardWithToolbarSx,
  rowTitleSx,
} from "../../components/listStyles";
import { displayDateOnly } from "../../util/dateOnly";
import {
  MemberLinks,
  RolePeriod,
  mutedTextSx,
  roleTypeLabel,
  sphereLabel,
} from "./positionRoleMeta";

// O "Novo" do breadcrumb cobre a criação — o "Criar" da toolbar repetia
// a mesma ação. Exportar: o padrão do react-admin para em 1.000 linhas e
// a base tem mais de 4 mil cargos (a API agora manda só id e nome dos
// sócios, então o arquivo completo é leve)
const ListActions = () => (
  <TopToolbar sx={{ alignItems: "center" }}>
    <ExportButton maxResults={10000} />
    <ListPageIndicator />
  </TopToolbar>
);

const filters = [
  <TextInput label="Buscar por cargo" source="name" alwaysOn resettable />,
];

// Exportação com colunas em português e só o nome dos sócios: o
// exportador padrão despejava os campos crus e, até a correção da API
// (TODO.md, item 26.1), o cadastro inteiro de cada sócio vinculado
const exporter = async (records: any[], fetchRelatedRecords: any) => {
  const related = (field: string, resource: string) =>
    records.some((r) => r[field])
      ? fetchRelatedRecords(records, field, resource)
      : Promise.resolve({});
  const [statuses, divisions] = await Promise.all([
    related("roleStatusId", "user-role-status/crud"),
    related("technicalDivisionId", "technical-divisions/crud"),
  ]);
  downloadCsvRows(
    toCsvRows(records, {
      ID: (r) => r.id,
      Cargo: (r) => r.name,
      Tipo: (r) => roleTypeLabel(r.roleType),
      "Cargo na comissão": (r) => r.secondaryRole,
      Esfera: (r) => sphereLabel(r.sphere),
      "Divisão Técnica": (r) => divisions[r.technicalDivisionId]?.name,
      Período: (r) => r.yearRange,
      Início: (r) => displayDateOnly(r.fromDate),
      Fim: (r) => displayDateOnly(r.toDate),
      Status: (r) => statuses[r.roleStatusId]?.name,
      Sócios: (r) =>
        (r.users || [])
          .map((u: any) => u?.fullName)
          .filter(Boolean)
          .join(", "),
    }),
    "cargos-e-funcoes"
  );
};

// Linha secundária do cargo: tipo e complementos (cargo na comissão,
// esfera, divisão técnica) em texto discreto, sem competir com o nome
const RoleSubtitle = ({ record }: { record: any }) => {
  const parts = [
    roleTypeLabel(record.roleType),
    record.secondaryRole,
    sphereLabel(record.sphere),
  ].filter(Boolean);
  if (parts.length === 0 && !record.technicalDivisionId) return null;
  return (
    <Typography component="div" sx={mutedTextSx}>
      {parts.join(" · ")}
      {record.technicalDivisionId && (
        <>
          {parts.length > 0 && " · "}
          <ReferenceField
            source="technicalDivisionId"
            reference="technical-divisions/crud"
            link={false}
          >
            <TextField
              source="name"
              sx={{ fontSize: "inherit", color: "inherit" }}
            />
          </ReferenceField>
        </>
      )}
    </Typography>
  );
};

// Padrão das listas do painel: clicar na linha abre a ficha;
// Editar/Excluir visíveis (antes ficavam atrás do menu "⋮"). A coluna
// antiga empilhava "Tipo de Cargo: / Nome do Cargo: / Status do Cargo:"
// em cada linha; agora cada informação tem sua coluna.
export const UserPositionRoleList = () => {
  return (
    <>
      <PageTopBreadcrumb
        title="Cargos e Funções"
        firstPath="/#/user-position-roles/crud"
        actionType="create"
        importButton={true}
        defaultModel="position-roles"
      />
      <List
        filters={filters}
        actions={<ListActions />}
        exporter={exporter}
        perPage={25}
        pagination={<Pagination rowsPerPageOptions={[10, 25, 50]} />}
        // Mantém a página atual visível enquanto a próxima carrega
        queryOptions={{ keepPreviousData: true }}
        sx={listCardWithToolbarSx}
      >
        <Datagrid
          rowClick="show"
          bulkActionButtons={<AppBulkDeleteButton />}
          sx={datagridSx}
        >
          <FunctionField
            label="Cargo"
            sortBy="name"
            render={(record: any) => (
              <Box>
                <Typography sx={rowTitleSx}>{record.name}</Typography>
                <RoleSubtitle record={record} />
              </Box>
            )}
          />
          <FunctionField
            label="Período"
            sortBy="yearRange"
            render={(record: any) => <RolePeriod record={record} />}
          />
          {/* A API não ordena por status nem por sócio */}
          <FunctionField
            label="Status"
            sortable={false}
            render={(record: any) =>
              record.roleStatusId ? (
                <ReferenceField
                  source="roleStatusId"
                  reference="user-role-status/crud"
                  link={false}
                >
                  <FunctionField
                    render={(status: any) => (
                      <RoleStatusChip name={status?.name} />
                    )}
                  />
                </ReferenceField>
              ) : (
                <Typography sx={mutedTextSx}>—</Typography>
              )
            }
          />
          <FunctionField
            label="Sócios"
            sortable={false}
            render={(record: any) => <MemberLinks members={record.users} />}
          />
          <FunctionField
            label="Ações"
            textAlign="right"
            render={() => <RaRowActions />}
          />
        </Datagrid>
      </List>
    </>
  );
};
